import React from 'react';
import { X, UserPlus, Sparkles, Save, Heart, Music } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSignUp: () => void;
  featureName?: string; // e.g., "save your jaap", "add to favourites"
}

export const GuestSignupPrompt: React.FC<Props> = ({ 
  isOpen, 
  onClose, 
  onSignUp,
  featureName = "save your progress"
}) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-stone-800 rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden border border-saffron/20 dark:border-stone-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-saffron via-orange-500 to-templeRed p-6 text-white">
          <button 
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X size={18} />
          </button>
          
          <div className="flex items-center justify-center mb-3">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
              <UserPlus size={32} className="text-white" />
            </div>
          </div>
          
          <h2 className="text-xl font-bold text-center">Create Your Account</h2>
          <p className="text-white/80 text-sm text-center mt-1">
            to {featureName}
          </p>
        </div>

        {/* Benefits */}
        <div className="p-6 space-y-4">
          <p className="text-gray-600 dark:text-stone-400 text-center text-sm">
            Sign up to unlock these divine features:
          </p>
          
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                <Save size={16} className="text-green-600 dark:text-green-400" />
              </div>
              <span className="text-gray-700 dark:text-stone-300">Save your jaap count permanently</span>
            </div>
            
            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <Heart size={16} className="text-red-500" />
              </div>
              <span className="text-gray-700 dark:text-stone-300">Create your favourite audio playlist</span>
            </div>
            
            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                <Sparkles size={16} className="text-purple-500" />
              </div>
              <span className="text-gray-700 dark:text-stone-300">Track your spiritual streaks</span>
            </div>
            
            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                <Music size={16} className="text-blue-500" />
              </div>
              <span className="text-gray-700 dark:text-stone-300">Sync across all your devices</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 pt-4">
            <button
              onClick={onSignUp}
              data-testid="guest-signup-btn"
              className="w-full py-3.5 px-4 rounded-2xl bg-templeRed dark:bg-saffron text-white dark:text-stone-900 font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-all shadow-lg"
            >
              <UserPlus size={20} />
              Sign Up Now
            </button>
            
            <button
              onClick={onClose}
              className="w-full py-3 px-4 rounded-2xl border-2 border-gray-200 dark:border-stone-700 text-gray-500 dark:text-stone-400 font-medium hover:bg-gray-50 dark:hover:bg-stone-700 transition-all text-sm"
            >
              Continue as Guest
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
